import { enabled } from "@giftforward/entitlements";
import {
  AddRounded,
  ArrowForwardRounded,
  CreditCardRounded,
  HistoryRounded,
  PauseCircleOutlined,
  PlayCircleOutlined,
  UndoRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import BuyCreditsModal from "@/components/usersOrgs/BuyCreditsModal";
import TransferCreditsModal from "@/components/usersOrgs/TransferCreditsModal";
import {
  useCreditLotEvents,
  useCreditLots,
  useCreditSummary,
  useHoldCreditLot,
  useReleaseCreditLot,
  useReverseCreditLot,
} from "@/hooks/useCredits";
import { useOrgEntitlements } from "@/hooks/useOrgEntitlements";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { usePermissions } from "@/hooks/usePermissions";
import { CREDIT_LOT_STATE, type CreditLot } from "@/interfaces/credits";
import { daysUntil } from "@/utils/date-format";

const fmtCredits = (s: string | number) => Number(s).toLocaleString("en-US");
// UTC so the calendar date matches the stored value regardless of the viewer's timezone.
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

type SortCol = "purchaseDate" | "expirationDate";

/** source_type → funding-source label (mirrors the backend SourceType enum). */
const FUNDING_SOURCE: Record<number, string> = {
  0: "Account Purchase",
  1: "Invoice",
  2: "Adjustment",
  3: "Transfer",
  4: "Auto Top-up",
};

/** Lot state → chip label + colors. */
type StateMeta = { label: string; bg: string; color: string };
const CONFIRMED_META: StateMeta = {
  label: "CONFIRMED",
  bg: "var(--color-good-sage-4)",
  color: "var(--color-good-sage)",
};
const STATE_META: Record<string, StateMeta> = {
  provisional: {
    label: "PROVISIONAL",
    bg: "var(--color-good-night-4)",
    color: "var(--color-good-night)",
  },
  confirmed: CONFIRMED_META,
  hold: { label: "ON HOLD", bg: "var(--color-good-ochre-4)", color: "var(--color-good-ochre)" },
  reversed: { label: "REVERSED", bg: "var(--color-good-wine-4)", color: "var(--color-good-wine)" },
};

/** State filter chips (single-select; "all" clears the filter). */
const STATE_CHIPS: { label: string; value: string }[] = [
  { label: "All", value: "all" },
  { label: "Confirmed", value: CREDIT_LOT_STATE.confirmed },
  { label: "Provisional", value: CREDIT_LOT_STATE.provisional },
  { label: "Hold", value: CREDIT_LOT_STATE.hold },
  { label: "Reversed", value: CREDIT_LOT_STATE.reversed },
];

/** Uppercase 11px stat: label over a big crimson value. */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--color-dark-dust)",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 30, lineHeight: 1 }}>
        {value}{" "}
        <Typography component="span" sx={{ fontSize: 14, color: "var(--color-graphite)" }}>
          credits
        </Typography>
      </Typography>
    </Box>
  );
}

const Sep = () => (
  <Box sx={{ width: "1px", height: 36, bgcolor: "var(--color-smoke)", flexShrink: 0 }} />
);

const th = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
  color: "var(--color-dark-dust)",
  borderBottom: "1px solid var(--color-card-border)",
  py: 1.25,
};
const td = { fontSize: 13.5, borderBottom: "1px solid var(--color-card-border)", py: 1.75 };

function StateChip({ lot }: { lot: CreditLot }) {
  const meta = STATE_META[lot.state] ?? CONFIRMED_META;
  const expired = daysUntil(lot.expirationDate) <= 0;
  const depleted =
    Number(lot.remainingCreditAmount) <= 0 && lot.state === CREDIT_LOT_STATE.confirmed;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
      <Box
        component="span"
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.05em",
          px: 1,
          py: 0.4,
          borderRadius: 9999,
          bgcolor: meta.bg,
          color: meta.color,
        }}
      >
        {meta.label}
      </Box>
      {expired && (
        <Typography sx={{ fontSize: 11, color: "var(--color-graphite)" }}>expired</Typography>
      )}
      {depleted && !expired && (
        <Typography sx={{ fontSize: 11, color: "var(--color-graphite)" }}>depleted</Typography>
      )}
    </Box>
  );
}

export default function GroupCreditsPoolPanel({
  groupId,
  groupName,
  organizationId,
  canPurchase,
  onAddPaymentMethod,
}: {
  groupId: number;
  groupName: string;
  /** The organization that OWNS this wallet — whose plan decides whether transfers are offered. */
  organizationId: number;
  canPurchase: boolean;
  /** Takes the client to where a card is added. Omit and the button falls back to disabled. */
  onAddPaymentMethod?: () => void;
}) {
  const { data: summary, isLoading } = useCreditSummary(groupId);
  // Scope: ACTIVE (has balance & not expired) by default; ALL includes hold/reversed/expired/depleted.
  const [scope, setScope] = useState<"active" | "all">("active");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const { can } = usePermissions();
  const canBuyCredit = can("buy", "CREDIT");
  const canSeeLots = can("send_invoice", "CREDIT");
  // null groupId disables the query — a caller who can't see lots shouldn't fire a request the
  // route will reject.
  const { data: lots = [] } = useCreditLots(canSeeLots ? groupId : null, scope === "all");
  const { data: cards = [] } = usePaymentMethods(groupId);
  const canBuy = canBuyCredit && canPurchase;
  /**
   * A transfer moves credit from one group to another, so it means nothing on a plan that sells a
   * single wallet: Starter and Pro clients have exactly one group, and the modal would offer them
   * a destination list containing only where they already are.
   *
   * Gated on `wallet.multi_group` — the same entitlement that decides whether OrgDetail shows the
   * group tree at all — rather than on a plan name, so the two surfaces can't disagree and an
   * Enterprise upgrade reveals both at once.
   *
   * Resolved against the organization that OWNS this wallet, never the caller: a GFG admin looking
   * at a Starter client's wallet is governed by that CLIENT's plan, so they see what the client
   * sees rather than their own unlimited one.
   */
  const { ent } = useOrgEntitlements(organizationId);
  const canTransfer = !!ent && enabled(ent, "wallet", "multi_group");
  // Manual lot actions (Hold / Release / Reverse) are a FINANCE capability, like invoice void/reverse.
  const canManage = can("cancel", "FINANCE");

  const hold = useHoldCreditLot(groupId);
  const release = useReleaseCreditLot(groupId);
  const [holdTarget, setHoldTarget] = useState<CreditLot | null>(null);
  const [reverseTarget, setReverseTarget] = useState<CreditLot | null>(null);
  const [historyLot, setHistoryLot] = useState<CreditLot | null>(null);

  const [buyOpen, setBuyOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  /** No card on file — buying is impossible until one exists, so the primary action changes. */
  const needsCard = cards.length === 0;
  const [cardId, setCardId] = useState<number | "">("");
  const selectedCard =
    cardId !== "" ? cardId : (cards.find((c) => c.isDefault)?.id ?? cards[0]?.id ?? "");
  const selectedCardObj = cards.find((c) => c.id === selectedCard) ?? null;

  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const filteredLots = useMemo(
    () => (stateFilter === "all" ? lots : lots.filter((l) => l.state === stateFilter)),
    [lots, stateFilter],
  );
  const sortedLots = useMemo(() => {
    if (!sortCol) return filteredLots;
    return [...filteredLots].sort((a, b) => {
      const diff = new Date(a[sortCol]).getTime() - new Date(b[sortCol]).getTime();
      return sortDir === "asc" ? diff : -diff;
    });
  }, [filteredLots, sortCol, sortDir]);
  const toggleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  return (
    <Box>
      {/* Summary bar: three stats · funding card · Buy credits */}
      <Box
        id="tour-wallet-summary"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          flexWrap: "wrap",
          bgcolor: "var(--color-white)",
          border: "1px solid var(--color-smoke)",
          borderRadius: "12px",
          p: "26px 30px",
          mb: 3,
        }}
      >
        {isLoading ? (
          <CircularProgress aria-label="Loading" size={22} sx={{ my: 0.5 }} />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            {/* THREE NUMBERS, because the first one alone is misleading. A wallet showing 2,500
                while every credit in it is headroom a live campaign already holds reads as money
                the client can spend, and the checkout that correctly refuses to offer it then looks
                broken. Balance is what the group HOLDS; reserved is what its campaigns are owed;
                available is the difference, and the only one of the three a new campaign can use. */}
            <Stat label="Wallet balance" value={fmtCredits(summary?.balance ?? "0")} />
            <Sep />
            <Stat
              label="Reserved by campaigns"
              value={fmtCredits(summary?.availableDetail?.budgeted ?? "0")}
            />
            <Sep />
            <Stat
              label="Available to a new campaign"
              value={fmtCredits(summary?.available ?? "0")}
            />
            <Sep />
            <Stat label="Expiring soon" value={fmtCredits(summary?.expiringSoon ?? "0")} />
          </Box>
        )}

        {canBuy && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: "auto", flexWrap: "wrap" }}>
            {/* Transfer sits apart from the buying pair. The card funds a PURCHASE and nothing
                else — a transfer spends no money — so the same Sep the stats use keeps the two
                ideas from reading as one control group. Absent entirely on a single-wallet plan:
                there is nowhere to transfer TO, so this is not a feature being withheld and an
                upsell would be misleading. */}
            {canTransfer && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<ArrowForwardRounded />}
                  onClick={() => setTransferOpen(true)}
                  sx={{ height: 44, whiteSpace: "nowrap" }}
                >
                  Transfer Credits
                </Button>
                <Sep />
              </>
            )}
            {cards.length > 0 && (
              <TextField
                select
                size="small"
                label="Funding card"
                value={selectedCard}
                onChange={(e) => setCardId(Number(e.target.value))}
                sx={{ minWidth: 170 }}
              >
                {cards.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {(c.brand ?? "Card").toUpperCase()} •• {c.last4 ?? "••••"}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {/* A greyed-out button says "not for you"; it does not say "do this first". With no
                card on file the same button in the same place becomes the step that IS available,
                so a client who has just arrived can follow it without knowing a card was ever a
                prerequisite. It only falls back to disabled when no destination was supplied. */}
            <Button
              id="tour-wallet-buy-btn"
              variant="contained"
              startIcon={needsCard ? <CreditCardRounded /> : <AddRounded />}
              onClick={() => (needsCard ? onAddPaymentMethod?.() : setBuyOpen(true))}
              disabled={needsCard && !onAddPaymentMethod}
              sx={{ bgcolor: "var(--color-dark-dust)", height: 44, whiteSpace: "nowrap" }}
            >
              {needsCard ? "Add Payment Method" : "Buy Credits"}
            </Button>
          </Box>
        )}
        {canBuyCredit && !canPurchase && (
          <Typography
            sx={{ ml: "auto", fontSize: 12.5, fontStyle: "italic", color: "var(--color-graphite)" }}
          >
            This group isn't enabled to purchase credits.
          </Typography>
        )}
      </Box>

      {/* Lot-level detail sits behind the same bar as the Invoices tab (send_invoice/CREDIT, a
          GFG-admin capability). The balances above stay on CREDIT/view — a client sees what they
          hold, not the provisional / hold / reversed mechanics behind it. */}
      {canSeeLots && (
        <>
          {/* Filters: ACTIVE <> ALL scope slider + state chips */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              flexWrap: "wrap",
              mb: 1.5,
              px: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  color: scope === "active" ? "var(--color-rodeo-black)" : "var(--color-graphite)",
                }}
              >
                ACTIVE
              </Typography>
              <Switch
                size="small"
                checked={scope === "all"}
                onChange={(e) => setScope(e.target.checked ? "all" : "active")}
                inputProps={{ "aria-label": "Active or all credit lots" }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  color: scope === "all" ? "var(--color-rodeo-black)" : "var(--color-graphite)",
                }}
              >
                ALL
              </Typography>
            </Box>
            <Box sx={{ width: "1px", height: 22, bgcolor: "var(--color-smoke)" }} />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {STATE_CHIPS.map((chip) => {
                const active = stateFilter === chip.value;
                return (
                  <Chip
                    key={chip.value}
                    label={chip.label}
                    size="small"
                    onClick={() => setStateFilter(chip.value)}
                    variant={active ? "filled" : "outlined"}
                    color={active ? "primary" : "default"}
                    sx={{ cursor: "pointer" }}
                  />
                );
              })}
            </Box>
          </Box>

          <Typography
            sx={{ fontSize: 12, color: "var(--color-graphite)", mb: 1.5, px: 0.5, lineHeight: 1.5 }}
          >
            <strong>Active</strong> shows Confirmed and Provisional lots with spendable credits (a
            spendable state, a balance remaining, and not expired). Held, Reversed, depleted, and
            expired lots appear under <strong>All</strong>.
          </Typography>

          {/* Lot table */}
          <Box
            sx={{
              bgcolor: "var(--color-white)",
              border: "1px solid var(--color-smoke)",
              borderRadius: "12px",
              p: "8px 24px 16px",
              overflowX: "auto",
            }}
          >
            {lots.length === 0 ? (
              <Typography
                sx={{ py: 5, textAlign: "center", fontSize: 13.5, color: "var(--color-graphite)" }}
              >
                {needsCard
                  ? "No credits yet. Add a payment method to buy your first credits."
                  : "No credits yet. Buy credits to fund this group's campaigns."}
              </Typography>
            ) : sortedLots.length === 0 ? (
              <Typography
                sx={{ py: 5, textAlign: "center", fontSize: 13.5, color: "var(--color-graphite)" }}
              >
                No lots match this filter.
              </Typography>
            ) : (
              <Table size="small" sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={th}>
                      <TableSortLabel
                        active={sortCol === "purchaseDate"}
                        direction={sortCol === "purchaseDate" ? sortDir : "asc"}
                        onClick={() => toggleSort("purchaseDate")}
                      >
                        Purchase Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={th}>Credits</TableCell>
                    <TableCell sx={th}>Status</TableCell>
                    <TableCell sx={th}>
                      <TableSortLabel
                        active={sortCol === "expirationDate"}
                        direction={sortCol === "expirationDate" ? sortDir : "asc"}
                        onClick={() => toggleSort("expirationDate")}
                      >
                        Expires
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={th}>Funding Source</TableCell>
                    <TableCell sx={{ ...th, textAlign: "right" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedLots.map((lot: CreditLot) => {
                    const remaining = Number(lot.remainingCreditAmount);
                    const original = Number(lot.originalCreditAmount);
                    const pct = original > 0 ? Math.round((remaining / original) * 100) : 0;
                    const d = daysUntil(lot.expirationDate);
                    const spendable =
                      lot.state === CREDIT_LOT_STATE.provisional ||
                      lot.state === CREDIT_LOT_STATE.confirmed;
                    const isHold = lot.state === CREDIT_LOT_STATE.hold;
                    const isReversed = lot.state === CREDIT_LOT_STATE.reversed;
                    return (
                      <TableRow key={lot.id}>
                        <TableCell sx={td}>{fmtDate(lot.purchaseDate)}</TableCell>
                        <TableCell sx={{ ...td, minWidth: 130 }}>
                          <Typography sx={{ fontSize: 14 }}>
                            <strong>{fmtCredits(remaining)} left</strong>
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, color: "var(--color-graphite)", mb: 0.5 }}
                          >
                            of {fmtCredits(original)}
                          </Typography>
                          <Box sx={{ height: 6, borderRadius: 3, bgcolor: "var(--color-wax)" }}>
                            <Box
                              sx={{
                                height: "100%",
                                width: `${pct}%`,
                                borderRadius: 3,
                                bgcolor: spendable
                                  ? "var(--color-dark-dust)"
                                  : "var(--color-smoke)",
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={td}>
                          <StateChip lot={lot} />
                        </TableCell>
                        <TableCell sx={td}>
                          {fmtDate(lot.expirationDate)}
                          <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                            {d > 0 ? `in ${d} day${d === 1 ? "" : "s"}` : "expired"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={td}>{FUNDING_SOURCE[lot.sourceType] ?? "—"}</TableCell>
                        <TableCell sx={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                          <Tooltip title="History" arrow>
                            <IconButton
                              size="small"
                              aria-label="Lot history"
                              onClick={() => setHistoryLot(lot)}
                            >
                              <HistoryRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canManage && spendable && (
                            <Tooltip title="Hold" arrow>
                              <IconButton
                                size="small"
                                aria-label="Hold lot"
                                onClick={() => setHoldTarget(lot)}
                              >
                                <PauseCircleOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canManage && isHold && (
                            <Tooltip title="Release" arrow>
                              <IconButton
                                size="small"
                                aria-label="Release lot"
                                disabled={release.isPending}
                                onClick={() => release.mutate({ lotId: lot.id })}
                              >
                                <PlayCircleOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canManage && !isReversed && (
                            <Tooltip title="Reverse (clawback)" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="Reverse lot"
                                onClick={() => setReverseTarget(lot)}
                              >
                                <UndoRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        </>
      )}

      {/* Mounted only while open, so every opening is a fresh read of what may be transferred.
          Kept mounted, the modal would show whatever the cap was the first time it was opened —
          and a campaign launched in between would not be reflected in it. */}
      {transferOpen && (
        <TransferCreditsModal
          open
          groupId={groupId}
          groupName={groupName}
          onClose={() => setTransferOpen(false)}
        />
      )}

      <BuyCreditsModal
        open={buyOpen}
        groupId={groupId}
        groupName={groupName}
        summary={summary}
        card={selectedCardObj}
        onClose={() => setBuyOpen(false)}
      />

      <ReasonDialog
        open={holdTarget != null}
        title="Hold credit lot"
        blurb="Pauses this lot — its credits are excluded from the spendable balance until released. Reversible."
        confirmLabel="Hold lot"
        confirmColor="primary"
        pending={hold.isPending}
        onClose={() => setHoldTarget(null)}
        onConfirm={(reason) =>
          holdTarget &&
          hold.mutate({ lotId: holdTarget.id, reason }, { onSuccess: () => setHoldTarget(null) })
        }
      />

      <ReverseLotDialog
        groupId={groupId}
        lot={reverseTarget}
        onClose={() => setReverseTarget(null)}
      />

      <LotHistoryDialog groupId={groupId} lot={historyLot} onClose={() => setHistoryLot(null)} />
    </Box>
  );
}

/** A reason-then-confirm dialog (Hold uses it directly; Reverse wraps it with a goodwill warning). */
function ReasonDialog({
  open,
  title,
  blurb,
  confirmLabel,
  confirmColor,
  pending,
  extra,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  blurb: string;
  confirmLabel: string;
  confirmColor: "primary" | "error";
  pending: boolean;
  extra?: React.ReactNode;
  error?: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const close = () => {
    setReason("");
    setLocalError(null);
    onClose();
  };
  const submit = () => {
    if (!reason.trim()) return setLocalError("Enter a reason.");
    setLocalError(null);
    onConfirm(reason.trim());
  };
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>{blurb}</Typography>
        {extra}
        <TextField
          label="Reason"
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          helperText="Recorded on the lot's activity log."
        />
        {(localError || error) && (
          <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13 }}>
            {localError || error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" color={confirmColor} onClick={submit} disabled={pending}>
          {pending ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Reverse = full clawback; spells out the consumed→goodwill consequence before committing. */
function ReverseLotDialog({
  groupId,
  lot,
  onClose,
}: {
  groupId: number;
  lot: CreditLot | null;
  onClose: () => void;
}) {
  const reverse = useReverseCreditLot(groupId);
  const [error, setError] = useState<string | null>(null);
  const remaining = lot ? Number(lot.remainingCreditAmount) : 0;
  const consumed = lot ? Number(lot.originalCreditAmount) - remaining : 0;
  return (
    <ReasonDialog
      open={lot != null}
      title="Reverse credit lot"
      blurb="Full clawback: the lot is removed from the balance and can't be undone (a later off-band payment on the source invoice would re-mint it)."
      confirmLabel="Reverse lot"
      confirmColor="error"
      pending={reverse.isPending}
      error={error}
      extra={
        lot ? (
          <Box
            sx={{
              border: "1px solid var(--color-smoke)",
              bgcolor: "var(--color-wax, #f7f5f1)",
              borderRadius: "8px",
              p: 1.5,
              fontSize: 13,
            }}
          >
            Reclaims <strong>{fmtCredits(remaining)}</strong> unspent.
            {consumed > 0 && (
              <>
                {" "}
                <strong>{fmtCredits(consumed)}</strong> already spent can't be recovered — written
                off to goodwill.
              </>
            )}
          </Box>
        ) : undefined
      }
      onClose={() => {
        setError(null);
        onClose();
      }}
      onConfirm={(reason) => {
        if (!lot) return;
        setError(null);
        reverse.mutate(
          { lotId: lot.id, reason },
          { onSuccess: onClose, onError: (e) => setError((e as Error).message) },
        );
      }}
    />
  );
}

/** Read-only per-lot activity history (system_event_logs, log_source "credit_lot"). */
function LotHistoryDialog({
  groupId,
  lot,
  onClose,
}: {
  groupId: number;
  lot: CreditLot | null;
  onClose: () => void;
}) {
  const { data: events = [], isLoading } = useCreditLotEvents(groupId, lot?.id ?? null);
  return (
    <Dialog open={lot != null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Lot history</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {isLoading ? (
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>Loading…</Typography>
        ) : events.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            No manual actions on this lot yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {events.map((ev) => (
              <Box key={ev.id} sx={{ display: "flex", gap: 1.25 }}>
                <Box
                  sx={{
                    mt: "5px",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "var(--color-dark-dust)",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, color: "var(--color-rodeo-black)" }}>
                    {ev.statusDescription ?? ev.eventType}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                    {dateTime(ev.createdAt)} · {ev.actorName ?? "System"}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
